<template>
  <div class="c-alert-secondary">
    <div class="wrapper">
      <div class="c-alert-secondary-info">
        <div
          class="c-alert-secondary__tag"
          v-if="alert && alert.fields && alert.fields.threatLevel && alert.fields.color"
        >
          Alert Level
          <span
            class="c-alert-secondary__level"
            :class="'c-alert-secondary__level--' + alert.fields.color.value.toLowerCase()"
          >
            {{ alert.fields.threatLevel.value.toLowerCase() }}
          </span>
        </div>
        <div
          class="c-alert-secondary__description"
          v-if="alert && alert.fields && alert.fields.alertLevelDescription"
        >
          {{ alert.fields.alertLevelDescription.value.split('.')[0] }}.
        </div>
        <sc-link
          v-if="fields && fields.link"
          :field="fields.link"
          class="c-cta--secondary"
          :class="fields.link.value.class"
        />
      </div>
      <div class="c-alert-secondary-levels">
        <sc-text
          class="c-alert-secondary__moreinfo"
          v-if="fields && fields.description"
          :field="fields.description"
        />
        <div
          class="alert-modal-link"
          v-if="fields && fields.modalContent"
          @click="largeModal = true"
        >
          Alert Levels
        </div>
      </div>
    </div>

    <template v-if="isEditing">
      <div class="c-modal">
        <div class="c-modal__content">
          <button class="c-modal__close" @click="largeModal = false">X</button>
          <sc-rich-text v-if="fields && fields.modalContent" :field="fields.modalContent" />
        </div>
      </div>
    </template>
    <template v-else>
      <VueFinalModal
        v-model="largeModal"
        class="final-modal largeModal"
        :class="{ scroll: isScrollable }"
        content-class="final-modal-content"
        content-transition="vfm-fade"
        overlay-transition="vfm-fade"
      >
        <div ref="modalContent" class="c-modal">
          <div class="c-modal__content">
            <button class="c-modal__close" @click="largeModal = false">X</button>
            <sc-rich-text v-if="fields && fields.modalContent" :field="fields.modalContent" />
          </div>
        </div>
      </VueFinalModal>
    </template>
  </div>
</template>

<script>
import { Text, Link, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { ref, reactive } from 'vue';
import { useWindowSize, useElementSize } from '@vueuse/core';
import { VueFinalModal } from 'vue-final-modal';
import 'vue-final-modal/style.css';

export default {
  name: 'CIS-Alert-Secondary',
  components: {
    ScText: Text,
    ScLink: Link,
    ScRichText: RichText,
    VueFinalModal
  },
  props: {
    fields: {
      type: Object
    },
    rendering: {
      type: Object
    }
  },
  setup() {
    // Window Height
    const { height } = useWindowSize();
    // Large Modal
    const largeModal = ref(false);
    // Modal Content
    const modalContent = ref(null);
    // Get Modal Content Size
    const modalSize = reactive(useElementSize(modalContent));

    return {
      height,
      largeModal,
      modalContent,
      modalSize
    };
  },
  computed: {
    isEditing() {
      return this.$jss.sitecoreContext().pageEditing;
    },
    alert() {
      return this.fields.alert;
    },
    isScrollable() {
      return this.modalSize?.height > this.height;
    }
  }
};
</script>
<style lang="scss" scoped>
.c-alert-secondary {
  @include padding-y(3rem);
  background-color: $box-gray;

  @include desktop-up {
    align-items: center;
  }

  .wrapper {
    @include padding-x-thin;
    @include desktop-up {
      display: flex;
    }
  }

  &-info {
    @include desktop-up {
      padding-right: 4rem;
      border-right: solid 0.1rem $divider;
    }
  }

  &-levels {
    text-align: right;
    margin-top: -33px;

    @include desktop-up {
      width: 33%;
      padding: 0 3rem 0 4rem;
      text-align: left;
      margin-top: 0;
    }
  }

  &__tag {
    @include font-styles('Arial Narrow', 2.4rem, 300, normal, 2.9rem, normal, $dark-blue);
  }

  &__level {
    @include font-styles('Arial Narrow', 2.2rem, 500, normal, 2.7rem, 0, $white);
    margin-left: 0.5rem;
    padding: 0.3rem 1.8rem 0.3rem 1.8rem;
    border-radius: 0.5rem;
    text-transform: capitalize;

    &--green {
      background-color: $alert-green;
    }

    &--blue {
      background-color: $alert-blue;
    }

    &--yellow {
      background-color: $alert-yellow;
    }

    &--orange {
      background-color: $alert-orange;
    }

    &--red {
      background-color: $alert-red;
    }
  }

  &__description {
    @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $dark-blue);
    margin: 2rem 0;
  }

  &__moreinfo {
    display: none;
    @include font-styles('Arial', 2rem, 500, normal, 2.5rem, 0, $dark-blue);
    margin-bottom: 3rem;

    @include desktop-up {
      display: block;
    }
  }
  .c-cta--secondary {
    margin-bottom: 0;
  }

  .alert-modal-link {
    display: inline-block;
    @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, normal, $blue);
    text-transform: uppercase;
    cursor: pointer;

    &:after {
      content: '';
      display: inline-block;
      width: 1.4rem;
      height: 1.4rem;
      margin-left: 1rem;
      background: $box-gray url(~@/assets/images/icon-expand.svg) no-repeat 50%;
      background-size: 100%;
    }
  }
}
</style>
